import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import AddIcons from '@/icons/AddIcons'
import DeleteIcons from '@/icons/DeleteOutlineIcons'
import { batchCreateOperationExpenses } from '@/modules/Report/components/OperationExpenses/services'
import {
    BatchExpenseRequest,
    OPERATION_EXPENSE_CATEGORIES,
} from '@/modules/Report/components/OperationExpenses/types'
import { ModalProps } from '@/types/shared'
import { yupResolver } from '@hookform/resolvers/yup'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { Button, Dialog, FormItem, IconButton } from 'vnetwork-ui'
import * as yup from 'yup'

export interface CreateOperationExpenseModalProps extends ModalProps {
    refetch?: () => void
}

interface FormData {
    expense_year: number
    expense_month: number
    currency: string
    expenses: BatchExpenseRequest[]
}

const currentYear = dayjs().year()
const CreateOperationExpenseModal: React.FC<
    CreateOperationExpenseModalProps
> = ({ open, onClose, refetch }) => {
    const { t } = useTranslation()
    const [isSubmitting, setIsSubmitting] = useState(false)

    // Form schema
    const schema = useMemo(
        () =>
            yup.object().shape({
                expense_year: yup.number().required(t('required')),
                expense_month: yup.number().required(t('required')),
                currency: yup.string().required(t('required')),
                expenses: yup.array().of(
                    yup.object().shape({
                        expense_name: yup.string().required(t('required')),
                        category: yup.string().required(t('required')),
                        amount: yup.number().min(0).required(t('required')),
                        notes: yup.string(),
                        description: yup.string(),
                    })
                ),
            }),
        [t]
    )

    const defaultValues: FormData = useMemo(
        () => ({
            expense_year: currentYear,
            expense_month: dayjs().month() + 1,
            currency: 'VND',
            expenses: [
                {
                    expense_name: '',
                    category: 'general_operations',
                    amount: 0,
                    notes: '',
                    description: '',
                },
            ],
        }),
        []
    )

    const formControl = useForm<FormData>({
        defaultValues,
        // @ts-ignore
        resolver: yupResolver(schema),
    })

    const { control, handleSubmit, reset } = formControl

    const {
        fields: expenseFields,
        append: appendExpense,
        remove: removeExpense,
    } = useFieldArray({
        control,
        name: 'expenses',
    })

    // Reset form when dialog opens/closes
    useEffect(() => {
        if (open) {
            reset(defaultValues)
        }
    }, [open, reset, defaultValues])

    // Category options
    const categoryOptions = useMemo(
        () =>
            OPERATION_EXPENSE_CATEGORIES.map((category) => ({
                label: t(`expense_category_${category}`),
                value: category,
            })),
        [t]
    )
    const { mutate: create } = useMutationWithHandler(
        {
            mutationFn: batchCreateOperationExpenses,
        },
        {
            onSuccess: () => {
                onClose()
                refetch?.()
            },
            reactQueryKeys: [QUERY_KEYS.REPORTS_BI_OPERATION_EXPENSES],
            successMessage: t('create-success'),
            errorMessage: t('something went wrong'),
        }
    )

    const onSubmit = async (values: FormData) => {
        try {
            setIsSubmitting(true)

            // Filter out empty items
            const expenses = values.expenses.filter(
                (e) => e.expense_name && e.amount > 0
            )

            if (expenses.length === 0) {
                toast.error(t('at_least_one_expense_required'))
                setIsSubmitting(false)
                return
            }

            // Call batch create API
            await create({
                report_month: `${values.expense_year}-${String(
                    values.expense_month
                ).padStart(2, '0')}`,
                currency: values.currency,
                expenses: expenses,
            })
        } catch (error) {
            toast.error(t('something went wrong'))
            console.error('Failed to create operation expenses:', error)
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title={t('create_operation_expenses')}
            maxWidth="lg"
        >
            <form
                onSubmit={handleSubmit(onSubmit as any)}
                className="space-y-4 p-4"
            >
                {/* Basic Info Section */}
                <div className="grid grid-cols-2 gap-4 pb-4 border-b">
                    <FormItem
                        control={control}
                        name="expense_year"
                        type="select"
                        label={t('expense_year')}
                        options={Array.from({ length: 12 }, (_, index) => ({
                            label: `${t('year')} ${currentYear - index}`,
                            value: currentYear - index,
                        }))}
                        allowClear={false}
                    />
                    <FormItem
                        control={control}
                        name="expense_month"
                        type="select"
                        label={t('expense_month')}
                        options={Array.from({ length: 12 }, (_, index) => ({
                            label: `${t('month')} ${index + 1}`,
                            value: index + 1,
                        }))}
                        allowClear={false}
                    />
                </div>

                {/* Expenses Section */}
                <div className="space-y-2">
                    <div className="flex justify-between items-center">
                        <h3 className="text-base font-medium text-red-700">
                            {t('operation_expenses')}
                        </h3>
                        <Button
                            type="button"
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<AddIcons />}
                            onClick={() =>
                                appendExpense({
                                    expense_name: '',
                                    category: 'general_operations',
                                    amount: 0,
                                    notes: '',
                                    description: '',
                                })
                            }
                        >
                            {t('add_expense')}
                        </Button>
                    </div>
                    <div className="space-y-2">
                        {expenseFields.map((field, index) => (
                            <div
                                key={field.id}
                                className="grid grid-cols-12 gap-2 items-start"
                            >
                                <div className="col-span-3">
                                    <FormItem
                                        control={control}
                                        name={`expenses.${index}.category`}
                                        type="select"
                                        label={index === 0 ? t('category') : ''}
                                        options={categoryOptions}
                                        allowClear={false}
                                    />
                                </div>
                                <div className="col-span-3">
                                    <FormItem
                                        control={control}
                                        name={`expenses.${index}.expense_name`}
                                        type="textfield"
                                        label={
                                            index === 0 ? t('expense_name') : ''
                                        }
                                        placeholder={t(
                                            'expense_name_placeholder'
                                        )}
                                    />
                                </div>
                                <div className="col-span-2">
                                    <FormItem
                                        control={control}
                                        name={`expenses.${index}.amount`}
                                        type="number"
                                        label={index === 0 ? t('amount') : ''}
                                        placeholder="0"
                                    />
                                </div>
                                <div className="col-span-3">
                                    <FormItem
                                        control={control}
                                        name={`expenses.${index}.notes`}
                                        type="textfield"
                                        label={index === 0 ? t('notes') : ''}
                                        placeholder={t('notes_placeholder')}
                                    />
                                </div>
                                <div className="col-span-1 flex items-center justify-center">
                                    <IconButton
                                        size="small"
                                        color="error"
                                        className="border hover:border-red-500 hover:text-red-500"
                                        onClick={() => removeExpense(index)}
                                    >
                                        <DeleteIcons />
                                    </IconButton>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-2 pt-4 border-t">
                    <Button
                        type="button"
                        variant="outlined"
                        onClick={onClose}
                        disabled={isSubmitting}
                    >
                        {t('cancel')}
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={isSubmitting}
                        loading={isSubmitting}
                    >
                        {t('create')}
                    </Button>
                </div>
            </form>
        </Dialog>
    )
}

export default CreateOperationExpenseModal
